import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Briefcase,
  MapPin,
  Buildings,
  Clock,
  GraduationCap,
  ListChecks,
  Star,
  CurrencyDollar,
  FileText,
  Trash,
} from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const JobDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchJob();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchJob = async () => {
    try {
      const response = await axios.get(`${API}/job-descriptions/${id}`);
      setJob(response.data);
    } catch (error) {
      console.error('Error fetching job:', error);
      toast.error('Failed to load job description');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/job-descriptions/${id}`);
      toast.success('Job description deleted');
      navigate('/jobs');
    } catch (error) {
      console.error('Error deleting job:', error);
      toast.error('Failed to delete job description');
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const InfoSection = ({ icon: Icon, title, content }) => {
    if (!content) return null;
    return (
      <div className="bg-white border border-slate-200 rounded-lg p-6">
        <div className="flex items-start space-x-3">
          <div className="bg-blue-50 text-[#002FA7] p-3 rounded-md">
            <Icon size={24} weight="duotone" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-medium text-slate-800 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {title}
            </h3>
            <p className="text-base text-slate-600 leading-relaxed whitespace-pre-wrap">{content}</p>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="pt-24 pb-12 px-6 sm:px-8 lg:px-12">
        <div className="max-w-5xl mx-auto">
          <div className="skeleton h-96 bg-white border border-slate-200 rounded-lg" />
        </div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="pt-24 pb-12 px-6 sm:px-8 lg:px-12">
        <div className="max-w-5xl mx-auto">
          <div className="bg-white border border-slate-200 rounded-lg p-12 text-center">
            <p className="text-base text-slate-600">Job description not found</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-24 pb-12 px-6 sm:px-8 lg:px-12" data-testid="job-detail-page">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate('/jobs')}
            className="flex items-center space-x-2 text-slate-600 hover:text-[#002FA7] transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft size={20} />
            <span className="text-sm font-medium">Back to Job Descriptions</span>
          </button>

          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="flex items-center space-x-2 px-4 py-2 border border-red-200 text-red-600 rounded-md text-sm font-medium hover:bg-red-50 hover:border-red-300 transition-colors"
            data-testid="delete-job-button"
          >
            <Trash size={16} weight="regular" />
            <span>Delete Job</span>
          </button>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-8 mb-6">
          <div className="flex items-start space-x-4">
            <div className="bg-gradient-to-br from-[#002FA7] to-[#0284C7] p-4 rounded-lg">
              <Briefcase size={48} weight="duotone" className="text-white" />
            </div>
            <div className="flex-1">
              <h1
                className="text-4xl font-medium tracking-tight text-slate-900 mb-2"
                style={{ fontFamily: 'Outfit, sans-serif' }}
                data-testid="job-title"
              >
                {job.title || 'Untitled Role'}
              </h1>
              {job.department && (
                <p className="text-lg text-slate-600 mb-4">{job.department}</p>
              )}
              <div className="flex flex-wrap gap-3">
                {job.location && (
                  <div className="flex items-center space-x-1.5 px-3 py-1.5 bg-slate-100 rounded-md text-sm text-slate-700">
                    <MapPin size={14} />
                    <span>{job.location}</span>
                  </div>
                )}
                {job.employment_type && (
                  <div className="flex items-center space-x-1.5 px-3 py-1.5 bg-blue-50 rounded-md text-sm text-blue-700">
                    <Briefcase size={14} />
                    <span>{job.employment_type}</span>
                  </div>
                )}
                {job.experience_required && (
                  <div className="flex items-center space-x-1.5 px-3 py-1.5 bg-slate-100 rounded-md text-sm text-slate-700">
                    <Clock size={14} />
                    <span>{job.experience_required}</span>
                  </div>
                )}
                {job.salary_range && (
                  <div className="flex items-center space-x-1.5 px-3 py-1.5 bg-emerald-50 rounded-md text-sm text-emerald-700">
                    <CurrencyDollar size={14} />
                    <span>{job.salary_range}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {job.description && (
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-6 mb-6">
            <h2 className="text-xl font-medium text-slate-800 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Role Overview
            </h2>
            <p className="text-base text-slate-700 leading-relaxed">{job.description}</p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <InfoSection icon={Star} title="Required Skills" content={job.required_skills} />
          <InfoSection icon={Star} title="Preferred Skills" content={job.preferred_skills} />
          <InfoSection icon={ListChecks} title="Responsibilities" content={job.responsibilities} />
          <InfoSection icon={ListChecks} title="Qualifications" content={job.qualifications} />
          <InfoSection icon={GraduationCap} title="Education Required" content={job.education_required} />
          <InfoSection icon={Buildings} title="Department" content={job.department} />
        </div>

        {job.original_content && (
          <div className="mt-6 bg-white border border-slate-200 rounded-lg p-6">
            <div className="flex items-center space-x-3 mb-4">
              <FileText size={24} weight="duotone" className="text-slate-500" />
              <h3 className="text-lg font-medium text-slate-800" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Original Content
              </h3>
              {job.source_file_name && (
                <span className="text-xs text-slate-500">({job.source_file_name})</span>
              )}
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded p-4 max-h-96 overflow-y-auto">
              <pre className="text-sm text-slate-700 whitespace-pre-wrap font-sans leading-relaxed">
                {job.original_content}
              </pre>
            </div>
          </div>
        )}

        {showDeleteConfirm && (
          <div
            className="fixed inset-0 bg-slate-900/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => !deleting && setShowDeleteConfirm(false)}
            data-testid="delete-job-confirm-modal"
          >
            <div
              className="bg-white rounded-lg shadow-2xl w-full max-w-md p-8"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center space-x-3 mb-4">
                <div className="bg-red-50 p-3 rounded-lg">
                  <Trash size={24} weight="duotone" className="text-red-600" />
                </div>
                <h3 className="text-xl font-medium text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Delete Job Description?
                </h3>
              </div>
              <p className="text-base text-slate-600 mb-6">
                Are you sure you want to delete <strong className="text-slate-900">{job.title || 'this job'}</strong>? This cannot be undone.
              </p>
              <div className="flex items-center justify-end space-x-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={deleting}
                  className="px-4 py-2 border border-slate-300 text-slate-700 rounded-md text-sm font-medium hover:bg-slate-50 transition-colors disabled:opacity-50"
                  data-testid="cancel-delete-job-button"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="px-4 py-2 bg-red-600 text-white rounded-md text-sm font-medium hover:bg-red-700 transition-colors disabled:opacity-50"
                  data-testid="confirm-delete-job-button"
                >
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default JobDetail;
